import loadable from '@loadable/component';
import { useState } from 'react';
import { RouterLink } from '@redux-first-router-link';

import {
  Grid,
  Box,
  Text,
  Button,
  Heading,
  useTheme,
} from '@simplywallst/ui-core';
import LockSolid from '@simplywallst/ui-core/icons/LockSolid';

import { ROUTE_PORTFOLIO } from '@/constants/routes';

import { useBrazeLimitHitterEvent } from '@/components/Braze/hooks/useBrazeLimitHitterEvent';
import { useUserAccess } from '@/hooks/useUserAccess';
import type { Portfolio } from '@/hooks/portfolio/usePortfolio';

import { pluralise } from '@/utilities/pluralise';

import { Container } from './styled';

const PlansModal = loadable(
  () => import('@/components/OurPlans/components/PlansModal/PlansModal')
);

type Props = {
  portfolio: Portfolio;
};

export const RestrictedState = ({ portfolio }: Props) => {
  const theme = useTheme();
  const { logLimitHitterEvent } = useBrazeLimitHitterEvent();

  const [openPlansModal, setOpenPlansModal] = useState(false);
  const { data: userAccess } = useUserAccess();

  return (
    <>
      <RouterLink
        to={{
          type: ROUTE_PORTFOLIO,
          payload: { portfolioId: portfolio.id },
        }}
        noUnderline
      >
        <Container data-testid="portfolio-card-restricted-state">
          <Grid templateRows="80px auto" gap="8px">
            <Box paddingX="x3" paddingY="x2">
              <Text typography="baseStrong" isTruncated>
                {portfolio.name}
              </Text>
              <Text typography="tiny" color="textSofter">
                {`${portfolio.holdings.length} ${pluralise(
                  portfolio.holdings.length,
                  'holding',
                  'holdings'
                )}`}
              </Text>
            </Box>
            <Grid
              justifyItems="center"
              templateRows="repeat(3, auto)"
              gap="4px"
            >
              <LockSolid
                fill={theme.color.chart05}
                aria-label="Blocked content"
              />
              <Text typography="xSmall">Excluded due to plan limit</Text>
              <Button
                styleType="strongLink"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setOpenPlansModal(true);
                  logLimitHitterEvent({
                    trigger: 'portfolio',
                  });
                }}
              >
                <Text as="span" typography="xSmall">
                  Upgrade
                </Text>
              </Button>
            </Grid>
          </Grid>
        </Container>
      </RouterLink>
      {openPlansModal && (
        <PlansModal onClose={() => setOpenPlansModal(false)}>
          <Text>
            You’ve reached your portfolio limits on your {userAccess.planType}{' '}
            plan
          </Text>
          <Heading as="h2">Upgrade to access more Portfolios</Heading>
        </PlansModal>
      )}
    </>
  );
};
